import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PublishIcon from '@mui/icons-material/Publish'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import PublicIcon from '@mui/icons-material/Public'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import ApartmentIcon from '@mui/icons-material/Apartment'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import EditNoteIcon from '@mui/icons-material/EditNote'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Close'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import ViewListIcon from '@mui/icons-material/ViewList'
import SearchableSelect from 'components/SearchableSelect'
import FormBuilder from './components/FormBuilder'
import FormRenderer from './components/FormRenderer'
import { FORM_CATEGORIES, getCategoryLabel } from './formCategories'
import TemplateAssignments from './components/TemplateAssignments'
import FormTemplateSharingPanel from './components/FormTemplateSharingPanel'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { getFormTemplate, getFormGeneration, updateFormTemplate as updateFormTemplateRequest, publishFormTemplate as publishFormTemplateRequest } from 'services/taskService'
import { toast } from 'react-toastify'
import GenerateFormFromDocument, { FormGenButton } from './components/GenerateFormFromDocument'
import useManagedBuildings from './components/GenerateFormFromDocument/useManagedBuildings'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import AiDraftBanner from './components/GenerateFormFromDocument/AiDraftBanner'
import AttachDraftDialog from './components/GenerateFormFromDocument/AttachDraftDialog'
import { serverMessage } from './components/GenerateFormFromDocument/formGeneration'
import {
  selectFormTemplates,
  selectFormTemplatesLoading,
  selectCurrentFormTemplate,
  selectCurrentFormTemplateLoading,
  selectFormTemplateSaving
} from './ducks/selectors'
import {
  getFormTemplatesRoutine,
  getFormTemplateRoutine,
  createFormTemplateRoutine,
  updateFormTemplateRoutine,
  deleteFormTemplateRoutine,
  publishFormTemplateRoutine,
  cloneFormTemplateRoutine
} from './ducks/actions'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { getFileTypesRoutine } from 'ducks/files/actions'
import { getFileTypes } from 'ducks/files/selectors'

const statusConfig = {
  draft: { color: '#b45309', bg: '#fef3c7', label: 'Draft', Icon: EditNoteIcon },
  published: { color: '#059669', bg: '#d1fae5', label: 'Published', Icon: CheckCircleOutlineIcon },
  archived: { color: '#6b7280', bg: '#f3f4f6', label: 'Archived', Icon: ArchiveOutlinedIcon }
}

const accessSourceConfig = {
  global: { color: '#33becc', bg: '#e6f7f9', label: 'System form', Icon: PublicIcon },
  owned: { color: '#7c3aed', bg: '#ede9fe', label: 'Your form', Icon: PersonOutlineIcon },
  shared: { color: '#c2410c', bg: '#ffedd5', label: 'Shared with you', Icon: PersonOutlineIcon },
  organisation: { color: '#4b5563', bg: '#f1f1f4', label: 'From your organisation', Icon: ApartmentIcon },
  building_passport: { color: '#0891b2', bg: '#cffafe', label: 'Used on your buildings', Icon: PersonOutlineIcon },
  admin: { color: '#db2777', bg: '#fce7f3', label: 'Admin', Icon: AdminPanelSettingsIcon }
}

const FormTemplates = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()

  const templates = useSelector(selectFormTemplates)
  const templatesLoading = useSelector(selectFormTemplatesLoading)
  const currentTemplate = useSelector(selectCurrentFormTemplate)
  const currentTemplateLoading = useSelector(selectCurrentFormTemplateLoading)
  const saving = useSelector(selectFormTemplateSaving)
  const fileTypes = useSelector(getFileTypes) || []

  const [creating, setCreating] = useState(false)
  const [activeMenu, setActiveMenu] = useState(null)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [scopeFilter, setScopeFilter] = useState('')
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('formTemplates.viewMode') || 'list')
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [sharingTemplate, setSharingTemplate] = useState(null)
  const [sharingLoading, setSharingLoading] = useState(false)

  const openSharing = async template => {
    setSharingTemplate(template)
    setSharingLoading(true)
    try {
      const res = await getFormTemplate(template.id)
      setSharingTemplate(res?.data?.data || template)
    } catch (e) {
      // keep the lighter template; the panel still works from list fields
    } finally {
      setSharingLoading(false)
    }
  }

  const shareSummary = t => {
    let base
    if (t.visibility === 'private') base = 'Only you'
    else if (t.visibility === 'organisation') base = 'Whole organisation'
    else if (t.visibility === 'shared') {
      base = t.sharedWithCount
        ? `Shared with ${t.sharedWithCount} ${t.sharedWithCount === 1 ? 'person' : 'people'}`
        : 'Shared with specific people'
    } else base = 'Private'
    return t.buildingScope === 'selected' ? `${base} · set buildings` : base
  }

  const isEditing = !!id
  const query = useMemo(() => new URLSearchParams(location.search), [location.search])
  const isAiDraft = query.get('aiDraft') === '1'
  const generationId = query.get('generationId')
  const contextBuildingId = query.get('bp') || location.state?.buildingPassportId || null
  const [draftInfo, setDraftInfo] = useState(null)
  const managedBuildings = useManagedBuildings(!isEditing)
  const [publishingDraft, setPublishingDraft] = useState(false)
  const [attachOpen, setAttachOpen] = useState(false)
  const draftTarget = {
    targetType: query.get('targetType') || draftInfo?.targetType || null,
    targetId: query.get('targetId') || draftInfo?.targetId || null,
    buildingPassportId: draftInfo?.buildingPassportId || null
  }

  useEffect(() => {
    if (!isAiDraft || !generationId) return undefined
    let live = true
    getFormGeneration(generationId)
      .then(res => { if (live) setDraftInfo(res?.data?.data || null) })
      .catch(() => {})
    return () => { live = false }
  }, [isAiDraft, generationId])

  useEffect(() => {
    dispatch(getFileTypesRoutine())
  }, [dispatch])

  useEffect(() => {
    const trimmed = search.trim()
    const t = setTimeout(() => {
      dispatch(getFormTemplatesRoutine({
        search: trimmed || undefined,
        category: categoryFilter || undefined,
        scope: scopeFilter || undefined
      }))
    }, trimmed ? 250 : 0)
    return () => clearTimeout(t)
  }, [dispatch, search, categoryFilter, scopeFilter])

  useEffect(() => {
    localStorage.setItem('formTemplates.viewMode', viewMode)
  }, [viewMode])

  useEffect(() => {
    if (!previewTemplate) return
    const onKey = (e) => { if (e.key === 'Escape') setPreviewTemplate(null) }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [previewTemplate])

  const visibleTemplates = useMemo(() => templates || [], [templates])

  useEffect(() => {
    if (isEditing) {
      dispatch(getFormTemplateRoutine(id))
    }
  }, [dispatch, id, isEditing])

  useEffect(() => {
    const handleClickOutside = () => setActiveMenu(null)
    if (activeMenu) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [activeMenu])

  const handleCreate = (data) => {
    const { autoPopulateMap, fileTypeId, ...rest } = data
    dispatch(createFormTemplateRoutine({
      ...rest,
      auto_populate_map: autoPopulateMap || null,
      file_type_id: fileTypeId || null,
      callback: () => setCreating(false)
    }))
  }

  const handleUpdate = (data) => {
    const { autoPopulateMap, fileTypeId, ...rest } = data
    dispatch(updateFormTemplateRoutine({
      id: currentTemplate.id,
      ...rest,
      auto_populate_map: autoPopulateMap || null,
      file_type_id: fileTypeId || null,
      callback: () => navigate(PRIVATE_PATHS.formBuilder)
    }))
  }

  const handlePublishDraft = async (data) => {
    const { autoPopulateMap, fileTypeId, ...rest } = data
    setPublishingDraft(true)
    try {
      await updateFormTemplateRequest({
        id: currentTemplate.id,
        ...rest,
        auto_populate_map: autoPopulateMap || null,
        file_type_id: fileTypeId || null
      })
      if (currentTemplate.status !== 'published') {
        await publishFormTemplateRequest(currentTemplate.id)
      }
      toast.success('Your form is published and ready to use.')
      dispatch(getFormTemplateRoutine(currentTemplate.id))
      if (draftTarget.targetType && draftTarget.targetId) {
        setAttachOpen(true)
      } else {
        navigate(PRIVATE_PATHS.formBuilder)
      }
    } catch (error) {
      toast.error(serverMessage(error))
    } finally {
      setPublishingDraft(false)
    }
  }

  const handleDelete = (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      dispatch(deleteFormTemplateRoutine(templateId))
    }
  }

  const handlePublish = (templateId) => {
    dispatch(publishFormTemplateRoutine(templateId))
  }

  const handleClone = (templateId) => {
    dispatch(cloneFormTemplateRoutine(templateId))
  }

  const handleEdit = (templateId) => {
    navigate(PRIVATE_PATHS.formBuilderEdit.replace(':id', templateId))
  }

  const handleBack = () => {
    if (creating) {
      setCreating(false)
    } else if (isEditing) {
      if (location.key && location.key !== 'default') {
        navigate(-1)
      } else {
        navigate(PRIVATE_PATHS.formBuilder)
      }
    }
  }

  // --- Create View ---
  if (creating) {
    return (
      <PageWrapper>
        <ContentContainer>
          <Header>
            <BackButton onClick={handleBack}>
              <ArrowBackIcon style={{ fontSize: 18 }} />
              Back to Templates
            </BackButton>
            <PageTitle>Create Form Template</PageTitle>
          </Header>
          <BuilderCard>
            <FormBuilder
              template={null}
              onSave={handleCreate}
              saving={saving}
              formTemplates={templates}
              fileTypes={fileTypes}
            />
          </BuilderCard>
        </ContentContainer>
      </PageWrapper>
    )
  }

  // --- Edit View ---
  if (isEditing) {
    if (currentTemplateLoading) {
      return (
        <PageWrapper>
          <ContentContainer>
            <Header>
              <BackButton onClick={handleBack}>
                <ArrowBackIcon style={{ fontSize: 18 }} />
                Back to Templates
              </BackButton>
            </Header>
            <SkeletonStack aria-busy='true' aria-label='Loading the form'>
              <SkeletonBar $w='40%' $h='28px' />
              <SkeletonBar $w='100%' $h='44px' />
              <SkeletonBar $w='100%' $h='72px' />
              <SkeletonBar $w='100%' $h='180px' />
            </SkeletonStack>
          </ContentContainer>
        </PageWrapper>
      )
    }

    if (!currentTemplate) {
      return (
        <PageWrapper>
          <ContentContainer>
            <Header>
              <BackButton onClick={handleBack}>
                <ArrowBackIcon style={{ fontSize: 18 }} />
                Back to Templates
              </BackButton>
            </Header>
            <EmptyState>
              <EmptyIconWrapper>
                <DescriptionOutlinedIcon />
              </EmptyIconWrapper>
              <EmptyTitle>Template not found</EmptyTitle>
            </EmptyState>
          </ContentContainer>
        </PageWrapper>
      )
    }

    const canEdit = currentTemplate.canEdit
    const accessSource = accessSourceConfig[currentTemplate.accessSource] || accessSourceConfig.owned
    const status = statusConfig[currentTemplate.status] || statusConfig.draft
    const AccessIcon = accessSource.Icon
    const StatusIcon = status.Icon

    return (
      <PageWrapper>
        <ContentContainer>
          <Header>
            <BackButton onClick={handleBack}>
              <ArrowBackIcon style={{ fontSize: 18 }} />
              Back to Templates
            </BackButton>
            <PageTitle>{canEdit ? 'Edit' : 'View'}: {currentTemplate.name}</PageTitle>
            <BadgeGroup>
              <Badge $color={accessSource.color} $bg={accessSource.bg}>
                <AccessIcon style={{ fontSize: 14 }} />
                {accessSource.label || (currentTemplate.creator ? `${currentTemplate.creator.firstName} ${currentTemplate.creator.lastName}`.trim() : 'Shared')}
              </Badge>
              <Badge $color={status.color} $bg={status.bg}>
                <StatusIcon style={{ fontSize: 14 }} />
                {status.label}
              </Badge>
            </BadgeGroup>
          </Header>

          {isAiDraft && <AiDraftBanner filename={draftInfo?.sourceFilename} />}

          {!canEdit && (
            <InfoBanner>
              <InfoOutlinedIcon />
              <span>
                This is a {currentTemplate.isGlobal ? 'global system' : 'read-only'} template.
                You can clone it to create your own editable version.
              </span>
            </InfoBanner>
          )}

          <BuilderCard>
            <FormBuilder
              template={currentTemplate}
              onSave={canEdit ? (isAiDraft ? handlePublishDraft : handleUpdate) : null}
              saving={isAiDraft ? publishingDraft : saving}
              saveLabel={isAiDraft ? 'Publish form' : undefined}
              formTemplates={templates}
              readOnly={!canEdit}
              fileTypes={fileTypes}
            />
          </BuilderCard>

          {attachOpen && (
            <AttachDraftDialog
              targetType={draftTarget.targetType}
              targetId={draftTarget.targetId}
              buildingPassportId={draftTarget.buildingPassportId}
              templateId={currentTemplate.id}
              onClose={() => setAttachOpen(false)}
              onDone={path => {
                setAttachOpen(false)
                navigate(path || PRIVATE_PATHS.formBuilder)
              }}
            />
          )}

          <SharingCard>
            <FormTemplateSharingPanel
              template={currentTemplate}
              onSaved={() => dispatch(getFormTemplateRoutine(currentTemplate.id))}
            />
          </SharingCard>

          {currentTemplate.renewalInterval && (
            <TemplateAssignments templateId={currentTemplate.id} />
          )}
        </ContentContainer>
      </PageWrapper>
    )
  }

  // --- List View ---
  return (
    <>
    <PageWrapper>
      <ContentContainer>
        <Header>
          <BackButton onClick={() => redirect(`${PRIVATE_PATHS.taskManagement}?tab=tasks`)}>
            <ArrowBackIcon style={{ fontSize: 18 }} />
            Back to Tasks
          </BackButton>
          <PageTitle>Form Templates</PageTitle>
          <HeaderButtons>
            {managedBuildings.options.length > 0 && (
              <GenerateFormFromDocument
                buildingPassportId={contextBuildingId && managedBuildings.options.some(o => String(o.value) === String(contextBuildingId)) ? contextBuildingId : null}
              >
                {open => (
                  <HeaderGenButton type='button' onClick={open} data-tour-id='formGen.open.library'>
                    <UploadFileRoundedIcon aria-hidden='true' />
                    <span>Create from a document</span>
                  </HeaderGenButton>
                )}
              </GenerateFormFromDocument>
            )}
            <CreateBtn onClick={() => setCreating(true)} data-tour-id='formTemplates.create'>
              <AddIcon style={{ fontSize: 18 }} />
              New Template
            </CreateBtn>
          </HeaderButtons>
        </Header>

        {(() => {
          const isFiltered = Boolean(search.trim()) || Boolean(categoryFilter) || Boolean(scopeFilter)
          const hasTemplates = visibleTemplates.length > 0

          if (templatesLoading && !hasTemplates) {
            return (
              <SkeletonStack aria-busy='true' aria-label='Loading forms'>
                {[0, 1, 2, 3].map(i => <SkeletonBar key={i} $w='100%' $h='88px' />)}
              </SkeletonStack>
            )
          }

          return (
            <>
              <Toolbar>
                <SearchWrapper>
                  <SearchIcon style={{ fontSize: 18 }} />
                  <SearchInput
                    type='text'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder='Search templates by name'
                  />
                  {search && (
                    <SearchClearBtn type='button' onClick={() => setSearch('')} aria-label='Clear search'>
                      <ClearIcon style={{ fontSize: 14 }} />
                    </SearchClearBtn>
                  )}
                </SearchWrapper>
                <CategoryFilter>
                  <SearchableSelect
                    value={categoryFilter}
                    onChange={v => setCategoryFilter(v)}
                    options={FORM_CATEGORIES.map(c => ({ value: c.value, label: c.label }))}
                    placeholder='All categories'
                    clearable
                    clearLabel='All categories'
                    searchPlaceholder='Search categories…'
                    ariaLabel='Filter by category'
                  />
                </CategoryFilter>
                <CategoryFilter>
                  <SearchableSelect
                    value={scopeFilter}
                    onChange={v => setScopeFilter(v)}
                    options={[
                      { value: 'hide_system', label: 'Hide system templates' },
                      { value: 'system_only', label: 'System templates only' },
                    ]}
                    placeholder='All templates'
                    clearable
                    clearLabel='All templates'
                    searchable={false}
                    ariaLabel='Show, hide, or only show system-provided templates'
                  />
                </CategoryFilter>
                <ViewToggle>
                  <ViewToggleBtn type='button' $active={viewMode === 'grid'} onClick={() => setViewMode('grid')} title='Grid view'>
                    <ViewModuleIcon style={{ fontSize: 18 }} />
                  </ViewToggleBtn>
                  <ViewToggleBtn type='button' $active={viewMode === 'list'} onClick={() => setViewMode('list')} title='List view'>
                    <ViewListIcon style={{ fontSize: 18 }} />
                  </ViewToggleBtn>
                </ViewToggle>
              </Toolbar>

              {!hasTemplates && isFiltered && (
                <EmptyState>
                  <EmptyIconWrapper>
                    <DescriptionOutlinedIcon />
                  </EmptyIconWrapper>
                  <EmptyTitle>No templates match your filters</EmptyTitle>
                  <EmptyDescription>
                    Try clearing the search or selecting a different category.
                  </EmptyDescription>
                  <BackButton onClick={() => { setSearch(''); setCategoryFilter(''); setScopeFilter('') }}>
                    Clear filters
                  </BackButton>
                </EmptyState>
              )}

              {!hasTemplates && !isFiltered && (
                <EmptyState>
                  <EmptyIconWrapper>
                    <DescriptionOutlinedIcon />
                  </EmptyIconWrapper>
                  <EmptyTitle>No form templates yet</EmptyTitle>
                  <EmptyDescription>
                    Form templates define what information to collect. Create a template first,
                    then use it when creating tasks to assign standardised forms to your team.
                  </EmptyDescription>
                  <CreateBtn onClick={() => setCreating(true)}>
                    <AddIcon style={{ fontSize: 18 }} />
                    Create First Template
                  </CreateBtn>
                </EmptyState>
              )}

              {hasTemplates && viewMode === 'grid' && (
                <TemplateGrid>
                  {visibleTemplates.map(template => {
                    const status = statusConfig[template.status] || statusConfig.draft
                    const accessSource = accessSourceConfig[template.accessSource] || accessSourceConfig.owned
                    const AccessIcon = accessSource.Icon
                    const StatusIcon = status.Icon
                    const categoryLabel = getCategoryLabel(template.category)

                    return (
                      <TemplateCard
                        key={template.id}
                        onClick={() => handleEdit(template.id)}
                      >
                        <CardHeader>
                          <CardIcon $isGlobal={template.isGlobal}>
                            <DescriptionOutlinedIcon />
                          </CardIcon>
                          <BadgeGroup>
                            <Badge $color={accessSource.color} $bg={accessSource.bg}>
                              <AccessIcon style={{ fontSize: 12 }} />
                              {accessSource.label || (template.creator ? `${template.creator.firstName} ${template.creator.lastName}`.trim() : 'Shared')}
                            </Badge>
                            <Badge $color={status.color} $bg={status.bg}>
                              <StatusIcon style={{ fontSize: 12 }} />
                              {status.label}
                            </Badge>
                          </BadgeGroup>
                        </CardHeader>

                        <CardName>{template.name}</CardName>
                        {template.description && (
                          <CardDescription>{template.description}</CardDescription>
                        )}

                        <CardMeta>
                          {categoryLabel && (
                            <>
                              <CategoryPill>{categoryLabel}</CategoryPill>
                              <MetaDot />
                            </>
                          )}
                          <MetaItem>{template.fieldCount || 0} fields</MetaItem>
                          <MetaDot />
                          <MetaItem>v{template.version || 1}</MetaItem>
                          {template.isGlobal && (
                            <>
                              <MetaDot />
                              <MetaItem $highlight>System Template</MetaItem>
                            </>
                          )}
                          {template.renewalInterval && (
                            <>
                              <MetaDot />
                              <MetaItem $highlight>
                                Renews every {template.renewalInterval} {template.renewalUnit}{template.renewalInterval > 1 ? 's' : ''}
                              </MetaItem>
                            </>
                          )}
                          {template.assignmentCount > 0 && (
                            <>
                              <MetaDot />
                              <MetaItem>{template.assignmentCount} assignment{template.assignmentCount !== 1 ? 's' : ''}</MetaItem>
                            </>
                          )}
                        </CardMeta>

                        {template.canShare && (
                          <ShareSummary onClick={e => { e.stopPropagation(); openSharing(template) }} title='Manage who can see and use this form'>
                            <PeopleOutlinedIcon style={{ fontSize: 13 }} />
                            <span>{shareSummary(template)}</span>
                          </ShareSummary>
                        )}

                        <CardActions onClick={e => e.stopPropagation()}>
                          <IconButton onClick={() => setPreviewTemplate(template)} title='Preview'>
                            <VisibilityOutlinedIcon style={{ fontSize: 16 }} />
                          </IconButton>
                          {template.canEdit && (
                            <IconButton onClick={() => handleEdit(template.id)} title='Edit'>
                              <EditIcon style={{ fontSize: 16 }} />
                            </IconButton>
                          )}
                          {template.canShare && (
                            <IconButton onClick={() => openSharing(template)} title='Share &amp; limit access'>
                              <PeopleOutlinedIcon style={{ fontSize: 16 }} />
                            </IconButton>
                          )}

                          <MenuWrapper>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation()
                                setActiveMenu(activeMenu === template.id ? null : template.id)
                              }}
                              title="More"
                            >
                              <MoreVertIcon style={{ fontSize: 16 }} />
                            </IconButton>

                            {activeMenu === template.id && (
                              <DropdownMenu>
                                {template.canClone && (
                                  <MenuItem onClick={() => { handleClone(template.id); setActiveMenu(null) }}>
                                    <ContentCopyIcon />
                                    <span>Clone</span>
                                  </MenuItem>
                                )}
                                {template.canPublish && (
                                  <MenuItem onClick={() => { handlePublish(template.id); setActiveMenu(null) }}>
                                    <PublishIcon />
                                    <span>Publish</span>
                                  </MenuItem>
                                )}
                                {template.canDelete && (
                                  <MenuItem $danger onClick={() => { handleDelete(template.id); setActiveMenu(null) }}>
                                    <DeleteOutlineIcon />
                                    <span>Delete</span>
                                  </MenuItem>
                                )}
                                {!template.canClone && !template.canPublish && !template.canDelete && (
                                  <MenuItemDisabled>No actions available</MenuItemDisabled>
                                )}
                              </DropdownMenu>
                            )}
                          </MenuWrapper>
                        </CardActions>
                      </TemplateCard>
                    )
                  })}
                </TemplateGrid>
              )}

              {hasTemplates && viewMode === 'list' && (
                <ListContainer>
                  <ListHeader>
                    <ListHeaderCell $grow>Name</ListHeaderCell>
                    <ListHeaderCell $w={200}>Category</ListHeaderCell>
                    <ListHeaderCell $w={120}>Status</ListHeaderCell>
                    <ListHeaderCell $w={90} $center>Fields</ListHeaderCell>
                    <ListHeaderCell $w={70} $center>Version</ListHeaderCell>
                    <ListHeaderCell $w={210}>Sharing</ListHeaderCell>
                    <ListHeaderCell $w={80} />
                  </ListHeader>
                  {visibleTemplates.map(template => {
                    const status = statusConfig[template.status] || statusConfig.draft
                    const accessSource = accessSourceConfig[template.accessSource] || accessSourceConfig.owned
                    const StatusIcon = status.Icon
                    const categoryLabel = getCategoryLabel(template.category)

                    return (
                      <ListRow key={template.id} onClick={() => handleEdit(template.id)}>
                        <ListCell $grow>
                          <ListNameLine>
                            <ListNameText title={template.name}>{template.name}</ListNameText>
                            {template.isGlobal && (
                              <Badge $color={accessSourceConfig.global.color} $bg={accessSourceConfig.global.bg}>System</Badge>
                            )}
                          </ListNameLine>
                          {template.description && (
                            <ListDescription title={template.description}>{template.description}</ListDescription>
                          )}
                        </ListCell>
                        <ListCell $w={200}>
                          {categoryLabel ? (
                            <CategoryPill>{categoryLabel}</CategoryPill>
                          ) : (
                            <MutedText>—</MutedText>
                          )}
                        </ListCell>
                        <ListCell $w={120}>
                          <Badge $color={status.color} $bg={status.bg}>
                            <StatusIcon style={{ fontSize: 12 }} />
                            {status.label}
                          </Badge>
                        </ListCell>
                        <ListCell $w={90} $center>{template.fieldCount || 0}</ListCell>
                        <ListCell $w={70} $center>v{template.version || 1}</ListCell>
                        <ListCell $w={210} onClick={e => e.stopPropagation()}>
                          {template.canShare ? (
                            <ShareSummary onClick={() => openSharing(template)} title='Manage who can see and use this form'>
                              <PeopleOutlinedIcon style={{ fontSize: 12 }} />
                              <span>{shareSummary(template)}</span>
                            </ShareSummary>
                          ) : (
                            <AccessInfo>
                              <Badge $color={accessSource.color} $bg={accessSource.bg}>{accessSource.label}</Badge>
                              {template.creator && (
                                <CreatorLine>by {`${template.creator.firstName || ''} ${template.creator.lastName || ''}`.trim()}</CreatorLine>
                              )}
                            </AccessInfo>
                          )}
                        </ListCell>
                        <ListCell $w={80} onClick={e => e.stopPropagation()}>
                          <ListActions>
                            <IconButton onClick={() => setPreviewTemplate(template)} title='Preview'>
                              <VisibilityOutlinedIcon style={{ fontSize: 16 }} />
                            </IconButton>
                            {template.canEdit && (
                              <IconButton onClick={() => handleEdit(template.id)} title='Edit'>
                                <EditIcon style={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                            <MenuWrapper>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setActiveMenu(activeMenu === template.id ? null : template.id)
                                }}
                                title='More'
                              >
                                <MoreVertIcon style={{ fontSize: 16 }} />
                              </IconButton>
                              {activeMenu === template.id && (
                                <DropdownMenu>
                                  {template.canClone && (
                                    <MenuItem onClick={() => { handleClone(template.id); setActiveMenu(null) }}>
                                      <ContentCopyIcon />
                                      <span>Clone</span>
                                    </MenuItem>
                                  )}
                                  {template.canPublish && (
                                    <MenuItem onClick={() => { handlePublish(template.id); setActiveMenu(null) }}>
                                      <PublishIcon />
                                      <span>Publish</span>
                                    </MenuItem>
                                  )}
                                  {template.canDelete && (
                                    <MenuItem $danger onClick={() => { handleDelete(template.id); setActiveMenu(null) }}>
                                      <DeleteOutlineIcon />
                                      <span>Delete</span>
                                    </MenuItem>
                                  )}
                                  {!template.canClone && !template.canPublish && !template.canDelete && (
                                    <MenuItemDisabled>No actions available</MenuItemDisabled>
                                  )}
                                </DropdownMenu>
                              )}
                            </MenuWrapper>
                          </ListActions>
                        </ListCell>
                      </ListRow>
                    )
                  })}
                </ListContainer>
              )}
            </>
          )
        })()}
      </ContentContainer>

    </PageWrapper>
    {previewTemplate && createPortal(
      <PreviewOverlay onClick={() => setPreviewTemplate(null)}>
        <PreviewDialog onClick={e => e.stopPropagation()} role='dialog' aria-modal='true'>
          <PreviewHeader>
            <PreviewHeaderText>
              <PreviewTitle>{previewTemplate.name}</PreviewTitle>
              <PreviewSubMeta>
                {getCategoryLabel(previewTemplate.category) && (
                  <CategoryPill>{getCategoryLabel(previewTemplate.category)}</CategoryPill>
                )}
                <PreviewMetaText>
                  {previewTemplate.fieldCount || 0} fields · v{previewTemplate.version || 1}
                  {previewTemplate.renewalInterval ? ` · Renews every ${previewTemplate.renewalInterval} ${previewTemplate.renewalUnit}${previewTemplate.renewalInterval > 1 ? 's' : ''}` : ''}
                </PreviewMetaText>
              </PreviewSubMeta>
              {previewTemplate.description && (
                <PreviewDescriptionText>{previewTemplate.description}</PreviewDescriptionText>
              )}
            </PreviewHeaderText>
            <PreviewCloseBtn type='button' onClick={() => setPreviewTemplate(null)} aria-label='Close preview'>
              <ClearIcon style={{ fontSize: 20 }} />
            </PreviewCloseBtn>
          </PreviewHeader>
          <PreviewBody>
            <FormRenderer
              schema={previewTemplate.schema || { sections: [] }}
              responses={{}}
              onAutoSave={() => {}}
              readOnly={true}
              isOnline={true}
            />
          </PreviewBody>
          <PreviewFooter>
            <PreviewFooterHint>Read-only preview · Press Esc to close</PreviewFooterHint>
            {previewTemplate.canEdit && (
              <PreviewFooterBtn type='button' onClick={() => { const id = previewTemplate.id; setPreviewTemplate(null); handleEdit(id) }}>
                Open in editor
              </PreviewFooterBtn>
            )}
          </PreviewFooter>
        </PreviewDialog>
      </PreviewOverlay>,
      document.body
    )}

    {sharingTemplate && createPortal(
      <PreviewOverlay onClick={() => setSharingTemplate(null)}>
        <PreviewDialog onClick={e => e.stopPropagation()} role='dialog' aria-modal='true'>
          <PreviewHeader>
            <PreviewHeaderText>
              <PreviewTitle>Share “{sharingTemplate.name}”</PreviewTitle>
              <PreviewMetaText>Choose who can see and use this form, and limit it to specific people or buildings.</PreviewMetaText>
            </PreviewHeaderText>
            <PreviewCloseBtn type='button' onClick={() => setSharingTemplate(null)} aria-label='Close'>
              <CloseRoundedIcon style={{ fontSize: 20 }} />
            </PreviewCloseBtn>
          </PreviewHeader>
          <PreviewBody>
            {sharingLoading ? (
              <div style={{ padding: 24, color: '#8a90a0' }}>Loading sharing settings…</div>
            ) : (
              <FormTemplateSharingPanel
                template={sharingTemplate}
                onSaved={() => {
                  dispatch(getFormTemplatesRoutine({
                    search: search.trim() || undefined,
                    category: categoryFilter || undefined,
                    scope: scopeFilter || undefined
                  }))
                  getFormTemplate(sharingTemplate.id)
                    .then(res => setSharingTemplate(res?.data?.data || sharingTemplate))
                    .catch(() => {})
                }}
              />
            )}
          </PreviewBody>
        </PreviewDialog>
      </PreviewOverlay>,
      document.body
    )}
    </>
  )
}

export default FormTemplates

// --- Animations ---

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`


const slideIn = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(-4px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`

// --- Styled Components ---

const PageWrapper = styled.div`
  position: fixed;
  top: ${({ theme }) => theme.dimensions.topBarHeight};
  left: ${({ theme }) => theme.dimensions.navWidth};
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.backgroundColor};
  overflow-x: hidden;
  overflow-y: auto;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 768px) {
    left: 0;
    top: 60px;
    bottom: 0;
  }
`

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px 80px;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    padding: 20px 16px 80px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 30px;
  flex-wrap: wrap;
`

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0;
  flex: 1;
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.06);
  }
`

const HeaderGenButton = styled(FormGenButton)`
  min-height: 42px;
  padding: 10px 20px;
  border-radius: 20px;
  border-color: ${({ theme }) => theme.colors.mediumGrey};
`

const CreateBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 22px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  background: ${({ theme }) => theme.colors.haiti};
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 0 15px rgba(41, 152, 163, 0.2);

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`

const SharingCard = styled.div`
  margin-top: 20px;
`

const BuilderCard = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 28px;
  box-shadow: 0 4px 9px rgba(0, 0, 0, 0.02);

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #e6f7f9;
  border: 1px solid #93c5fd;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #33becc;

  svg {
    font-size: 20px;
    flex-shrink: 0;
    color: #33becc;
  }
`

const BadgeGroup = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`

const skeletonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
`

const SkeletonBar = styled.div`
  width: ${({ $w }) => $w};
  height: ${({ $h }) => $h};
  border-radius: 10px;
  background: #eef1f5;
  animation: ${skeletonPulse} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`

const EmptyIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    font-size: 40px;
    color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  }
`

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 8px 0;
`

const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 0 0 24px 0;
`

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const TemplateCard = styled.div`
  background: white;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 9px rgba(0, 0, 0, 0.02);
  min-width: 0;

  &:hover {
    border-color: ${({ theme }) => `${theme.colors.primary}40`};
    transform: translateY(-2px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`

const CardIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ theme, $isGlobal }) => $isGlobal ? '#e6f7ff' : (theme.colors.linen || '#fbede9')};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 20px;
    color: ${({ theme, $isGlobal }) => $isGlobal ? '#1890ff' : theme.colors.primary};
  }
`

const CardName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 4px;
`

const CardDescription = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const MetaItem = styled.span`
  font-size: 12px;
  color: ${({ theme, $highlight }) => $highlight ? '#1890ff' : (theme.colors.grey?.[700] || '#b3b3b3')};
  font-weight: ${({ $highlight }) => $highlight ? '500' : 'normal'};
`

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
`

const ShareSummary = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 3px 7px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #5a6472;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 150ms ease, color 150ms ease;
  svg { flex: 0 0 auto; opacity: 0.75; }
  &:hover { background: #f2fbfb; color: #1f8b98; }
  span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`
const AccessInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-start;
`
const CreatorLine = styled.span`
  font-size: 11px;
  color: #8a90a0;
`
const CardActions = styled.div`
  display: flex;
  gap: 6px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  padding-top: 12px;
`

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ $muted, theme }) => $muted ? (theme.colors.grey?.[700] || '#b3b3b3') : theme.colors.haiti};
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
    border-color: ${({ theme }) => theme.colors.mediumGrey};
  }
`

const MenuWrapper = styled.div`
  position: relative;
  margin-left: auto;
`

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 160px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
  padding: 6px;
  z-index: 1000;
  animation: ${slideIn} 0.15s ease-out;
`

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: ${({ $danger, theme }) => $danger ? (theme.colors.error || '#d14925') : theme.colors.haiti};
  transition: background 0.1s;
  text-align: left;

  &:hover {
    background: ${({ $danger, theme }) => $danger ? (theme.colors.linen || '#fbede9') : theme.colors.lightGrey};
  }

  svg {
    font-size: 18px;
    color: ${({ $danger, theme }) => $danger ? (theme.colors.error || '#d14925') : (theme.colors.grey?.[800] || '#999')};
  }
`

const MenuItemDisabled = styled.div`
  padding: 10px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  text-align: center;
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 240px;
  padding: 8px 12px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 2px ${({ theme }) => `${theme.colors.secondary}20`};
  }

  svg {
    color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
    flex-shrink: 0;
  }
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  min-width: 0;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  }
`

const SearchClearBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }
`

const ViewToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  overflow: hidden;
  background: white;
`

const ViewToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: none;
  background: ${({ $active, theme }) => $active ? theme.colors.haiti : 'white'};
  color: ${({ $active, theme }) => $active ? 'white' : (theme.colors.grey?.[800] || '#666')};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ $active, theme }) => $active ? theme.colors.haiti : theme.colors.lightGrey};
  }

  & + & {
    border-left: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  }
`

const CategoryFilter = styled.div`
  position: relative;
  min-width: 220px;
`

const CategoryPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => `${theme.colors.primary}14`};
  border-radius: 12px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ListContainer = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 9px rgba(0, 0, 0, 0.02);
`

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-bottom: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#666'};
`

const ListHeaderCell = styled.div`
  ${({ $grow }) => $grow ? 'flex: 1; min-width: 0;' : ''}
  ${({ $w }) => $w ? `width: ${$w}px; flex-shrink: 0;` : ''}
  ${({ $center }) => $center ? 'text-align: center;' : ''}
  padding-right: 12px;

  &:last-child {
    padding-right: 0;
  }
`

const ListRow = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  cursor: pointer;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => `${theme.colors.lightGrey}80`};
  }
`

const ListCell = styled.div`
  ${({ $grow }) => $grow ? 'flex: 1; min-width: 0;' : ''}
  ${({ $w }) => $w ? `width: ${$w}px; flex-shrink: 0;` : ''}
  ${({ $center }) => $center ? 'text-align: center;' : ''}
  padding-right: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};

  &:last-child {
    padding-right: 0;
  }
`

const ListNameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const ListNameText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const ListDescription = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#777'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ListActions = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-end;
`

const MutedText = styled.span`
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
`

const PreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease-out;
`

const PreviewDialog = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  width: min(880px, 100%);
  max-height: 90vh;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  overflow: hidden;
`

const PreviewHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const PreviewHeaderText = styled.div`
  flex: 1;
  min-width: 0;
`

const PreviewTitle = styled.h2`
  margin: 0 0 6px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  overflow: hidden;
  text-overflow: ellipsis;
`

const PreviewSubMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const PreviewMetaText = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#777'};
`

const PreviewDescriptionText = styled.p`
  margin: 8px 0 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#777'};
`

const PreviewCloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.lightGrey};
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.mediumGrey};
  }
`

const PreviewBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: ${({ theme }) => theme.colors.backgroundColor || '#fafafa'};
`

const PreviewFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: white;
`

const PreviewFooterHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#999'};
`

const PreviewFooterBtn = styled.button`
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  color: white;
  background: ${({ theme }) => theme.colors.haiti};
  border: none;
  border-radius: 18px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
  }
`
